/**
 * PATH: packages/job-hub/src/jobs/tabs/JobItpLibraryColumns.tsx
 * FILE_ID: JOBHUB-JOBS-ITP-LIBRARY-COLUMNS-001
 * PURPOSE: Saved ITPs as two columns: Locations (expand to see their ITPs) and every ITP.
 * LANDMINE: No QA names (operator 2026-10-28). Same-named locations across QAs are one row.
 */
/* eslint-disable react-refresh/only-export-components -- row builder stays next to the columns */
import { useMemo, useState } from 'react'
import { ChevronRight, FileText } from 'lucide-react'
import type { QaItpLibraryEntry, QaItpLibraryInspection } from '../qa/qaApi'

export interface ItpLibraryRow { inspectionId: string; entry: QaItpLibraryEntry }
export interface ItpLibraryLocation { key: string; name: string; rows: ItpLibraryRow[] }

export function itpLibraryRows(inspections: readonly QaItpLibraryInspection[]) {
  const itps: ItpLibraryRow[] = []
  const byKey = new Map<string, ItpLibraryLocation>()
  for (const inspection of inspections) {
    for (const entry of inspection.entries) itps.push({ inspectionId: inspection.id, entry })
    for (const area of inspection.areas) {
      const name = area.area_name.trim()
      const key = name.toLowerCase()
      const entries = inspection.entries.filter((entry) => entry.area_ids.includes(area.id))
      if (!key || entries.length === 0) continue
      const location = byKey.get(key) ?? { key, name, rows: [] }
      for (const entry of entries) {
        if (!location.rows.some((row) => row.entry.id === entry.id)) {
          location.rows.push({ inspectionId: inspection.id, entry })
        }
      }
      byKey.set(key, location)
    }
  }
  return { itps, locations: [...byKey.values()] }
}

function ItpRowButton({ row, divided, onOpen }: { row: ItpLibraryRow; divided: boolean; onOpen: (row: ItpLibraryRow) => void }) {
  return (
    <button
      type="button"
      title={row.entry.name}
      onClick={() => onOpen(row)}
      className={`flex w-full min-w-0 flex-col items-start gap-0.5 py-2 text-left text-sm ${divided ? 'border-b border-border' : ''}`}
    >
      <span className="flex w-full min-w-0 items-center gap-1.5">
        <span className="min-w-0 truncate">{row.entry.name}</span>
        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </span>
      <span className="text-xs text-muted-foreground">{new Date(row.entry.saved_at).toLocaleString()}</span>
    </button>
  )
}

export function JobItpLibraryColumns({ inspections, capped, onOpen }: {
  inspections: readonly QaItpLibraryInspection[]
  capped: boolean
  onOpen: (row: ItpLibraryRow) => void
}) {
  const { itps, locations } = useMemo(() => itpLibraryRows(inspections), [inspections])
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(() => new Set())
  const toggle = (key: string) => setOpenKeys((current) => {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const listClass = capped ? 'max-h-[45vh] overflow-auto' : undefined
  return (
    <div data-testid="itp-library-columns" className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
      <section className="min-w-0 rounded-lg border border-border p-3">
        <p className="mb-1 text-xs text-muted-foreground">Locations</p>
        <div className={listClass}>
          {locations.length === 0 ? <p className="py-2 text-xs text-muted-foreground">No locations.</p> : null}
          {locations.map((location) => {
            const open = openKeys.has(location.key)
            return (
              <div key={location.key} className="border-b border-border">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggle(location.key)}
                  className="flex min-h-[44px] w-full min-w-0 items-center gap-2 py-2 text-left text-sm md:min-h-0"
                >
                  <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{location.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{location.rows.length}</span>
                </button>
                {open ? (
                  <div className="pb-1 pl-5">
                    {location.rows.map((row) => <ItpRowButton key={row.entry.id} row={row} divided={false} onOpen={onOpen} />)}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </section>
      <section className="min-w-0 rounded-lg border border-border p-3">
        <p className="mb-1 text-xs text-muted-foreground">ITPs</p>
        <div className={listClass}>
          {itps.map((row) => <ItpRowButton key={row.entry.id} row={row} divided onOpen={onOpen} />)}
        </div>
      </section>
    </div>
  )
}
